// src/admin/pages/AdminReportsTasksPage.jsx

import React, { useMemo, useState } from 'react';
import ObjectTopBar from '../components/topbar/ObjectTopBar';
import { WorkspaceDataView } from '../components/layout/WorkspaceView';
import DataTable from '../components/common/DataTable';
import DateTimeDisplay from '../components/common/DateTimeDisplay';
import PersonNameDisplay from '../components/common/PersonNameDisplay';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle';
import useAdminTaskReports, { getPreviousMonth, getNextMonth, formatMonth } from '../hooks/useAdminTaskReports';
import useTrackPageVisit from '../hooks/useTrackPageVisit';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';
import formatDurationShort from '../../shared/utils/formatDuration';
import { TASK_DURATION_BY_KEY, NOT_REPORTED, hoursToMs } from '../utils/taskDurations';

// A missing number is not a zero. Every numeric column on this table used to render
// `Number(x ?? 0)`, so a lead time with no start, a work time nobody reported and a crew size
// the workflow never specified all came out as a confident `0h` — and in the month I looked at,
// every single row read `Work 0h` with not one of them having reported anything. For a report
// whose job is validating invoices, that is the one mistake it must not make.
function notReported() {
  return <span className="text-muted" title={NOT_REPORTED}>—</span>;
}

// Hours from the API, rendered the way people read time. `formatDurationShort` floors to a
// single unit, so a two-minute job is "2m" rather than "0.033h" and a long wait is "3d" rather
// than "74h". The CSV keeps the decimal hours for whatever consumes it.
function durationCell(hours) {
  const formatted = formatDurationShort(hoursToMs(hours));
  if (formatted === null) return notReported();

  return <span title={`${hours}h`}>{formatted}</span>;
}

export default function AdminReportsTasksPage() {
  // Track page visit for onboarding
  useTrackPageVisit('reports');
  const defaultMonth = useMemo(() => {
    const d = new Date();
    // default to current month
    return formatMonth(d);
  }, []);

  const [month, setMonth] = useState(defaultMonth);
  const { data: rows, loading } = useAdminTaskReports(month);
  useDocumentTitle('Field Engineering Work Report – Maintella');

  const columns = [
    {
      key: 'workflow_name',
      label: 'Workflow',
      icon: 'bi-diagram-3',
      sortable: true,
      role: 'primary',
      // The version used to be a column of its own holding a row id ("Def. ID"). It qualifies
      // the workflow name rather than standing beside it, so it reads as a muted second line
      // under the name — the same shape as the open-tasks list. The CSV export still carries
      // the definition id, since that is a file somebody may already be joining on.
      render: (r) => (
        <div className="min-w-0">
          <div>{r.workflow_name || '—'}</div>
          {Number.isFinite(r.workflow_version) && (
            <div className="text-muted small">{`v${r.workflow_version}`}</div>
          )}
        </div>
      ),
      filterValue: (r) =>
        [r.workflow_name, Number.isFinite(r.workflow_version) && `v${r.workflow_version}`]
          .filter(Boolean)
          .join(' '),
    },
    // There was a "Published at" column here. It carried the *workflow's* `published_at` — when
    // that version of the workflow was published, which is a fact about the workflow and not
    // about the work this row reports. It was standing in for "which version was used", and the
    // version number above now says that directly. The CSV keeps the column: it is the only
    // thing in that file which dates a version, and the file is one people join on.
    {
      key: 'asset_name',
      label: 'Asset',
      sortable: true,
    },
    {
      key: 'task_assigned_to_email',
      label: 'Assigned To',
      icon: 'bi-person-check',
      sortable: true,
    },
    {
      key: 'completed_by_field_engineer',
      label: 'Completed By',
      icon: 'bi-person-fill-check',
      sortable: true,
      render: r => (
        <PersonNameDisplay 
          givenName={r.completed_by_field_engineer?.given_name}
          familyName={r.completed_by_field_engineer?.family_name}
        />
      ),
      filterValue: r => `${r.completed_by_field_engineer?.given_name || ''} ${r.completed_by_field_engineer?.family_name || ''}`.trim()
    },
    {
      key: 'workflow_reference_time_hours',
      label: 'Expected',
      icon: 'bi-clock',
      tooltip: 'The duration the workflow says the job should take.',
      sortable: true,
      render: r => durationCell(r.workflow_reference_time_hours),
      filterValue: r => r.workflow_reference_time_hours,
      className: 'text-nowrap'
    },
    {
      key: 'workflow_personnel_count',
      label: 'Crew',
      icon: 'bi-people',
      tooltip: 'The number of people the workflow asks for.',
      sortable: true,
      // `?? 0` here claimed a crew of nobody for a workflow that simply does not specify one.
      render: r => (Number.isFinite(r.workflow_personnel_count)
        ? r.workflow_personnel_count
        : notReported()),
      filterValue: r => r.workflow_personnel_count,
      className: 'text-nowrap'
    },
    {
      key: 'task_completed_at',
      label: 'Completed',
      icon: 'bi-check-circle',
      tooltip: 'When the task was closed.',
      sortable: true,
      render: r => <DateTimeDisplay isoString={r.task_completed_at} />,
      filterValue: r => r.task_completed_at,
      className: 'text-nowrap'
    },
    // The four time spans, in the order they happen. Labels and hover text come from
    // taskDurations so this table and a task's own report cannot describe them differently.
    ...['lead', 'on_site', 'cycle', 'work'].map((key) => {
      const { rowKey, label, definition } = TASK_DURATION_BY_KEY[key];
      return {
        key: rowKey,
        label,
        icon: key === 'work' ? 'bi-stopwatch' : 'bi-hourglass-split',
        tooltip: definition,
        sortable: true,
        render: r => durationCell(r[rowKey]),
        filterValue: r => r[rowKey],
        className: 'text-nowrap',
        // Three of these are wall-clock spans that a phone screen can do without; work time is
        // the one an operator is usually chasing.
        hideOnNarrow: key !== 'work',
      };
    }),
  ];

  async function handleDownloadCsv() {
    const url = `${API_URL}/v1/admin/reporting/tasks.csv?month=${encodeURIComponent(month)}`;
    const res = await fetchWithAuth(url);
    if (!res.ok) return; // could add toast later
    const blob = await res.blob();
    const a = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    a.href = objectUrl;
    a.download = `tasks_completed_${month}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  }

  return (
    <>
      <ObjectTopBar
        title="Field Engineering Work Report"
        primaryBtn={{
          btnText: 'Download CSV',
          btnIcon: 'bi bi-download',
          variant: 'outline-primary',
          onClick: handleDownloadCsv,
        }}
      />

      {/* Thirteen columns do not fit a centered `container`: it caps the body at 1320px and then
          centres what is left of it, so the widest table in the admin was getting the least
          room and every cell wrapped. The full-width frame is the one the AI tools use, and the
          month switcher rides in its toolbar so it stays put while the report scrolls. */}
      <WorkspaceDataView
        toolbar={(
          <div className="d-flex align-items-center gap-2 pb-3">
            <button className="btn btn-light" onClick={() => setMonth(getPreviousMonth(month))} type="button" aria-label="Previous month">
              <i className="bi bi-chevron-left" />
            </button>
            <div className="fw-semibold">{month}</div>
            <button className="btn btn-light" onClick={() => setMonth(getNextMonth(month))} type="button" aria-label="Next month">
              <i className="bi bi-chevron-right" />
            </button>
          </div>
        )}
      >
        <DataTable
          items={rows}
          columns={columns}
          filterPlaceholder="Search report…"
          emptyMessage={loading ? 'Loading…' : 'No results for this month.'}
          fill
          label="Field engineering work report"
        />
      </WorkspaceDataView>
    </>
  );
}
